// @ts-nocheck
import { CircularProgress, Box, useMediaQuery } from '@mui/material';
import { blue } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import { XGButton } from 'xg-cl';
import * as PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import './BookingPlanActions.css';
import { formatCheckoutCharge } from '../../../../utils/checkoutCurrency';
import { formatCurrency } from '../../../../utils/currencyHelper';
import { useBookingSteps } from '../../../../hooks/useBookingSteps';
import { getOperatorContactLink } from '../../../../utils/operatorContact';

/**
 * Render actions for booking plan component
 *
 * @namespace Components
 *
 * @param {function} onCancel - Callback function for cancel action
 * @param {function} onHandleNext - Callback function for submit action
 * @param {boolean} isSubmitting - Flag indicating if form is currently submitting
 * @return {JSX.Element} The booking plan actions component
 */
const BookingPlanActions = ({
  onCancel,
  onHandleNext,
  isSubmitting,
  disabledBack = false,
  disabledConfirm = false,
  isFullCapacity = false,
  operatorEmail = null,
  operatorContact = null,
  selectedCurrency = null,
  paymentRecoveryStatus = null
}) => {
  const { t } = useTranslation(['common', 'trip', 'bookings']);
  const booking = useSelector((state) => state.booking);
  const bookingTotals = booking?.bookingTotals || [];
  const color = useSelector((state) => state?.color || {});
  const primaryColor = color?.highlightColor || 'var(--primary-600, #1361d8)';
  const { activeStep, getCtaLabel } = useBookingSteps();
  const activeStepKey = activeStep?.key;
  const showBackButton = activeStepKey !== 'plan';
  const sortedBookingTotals =
    bookingTotals && bookingTotals.length
      ? [...bookingTotals].sort((a, b) => a?.order - b?.order)
      : [];

  // Extract payment data from bookingTotals for direct control
  const paymentAmountItem = sortedBookingTotals.find(
    (item) => item.key === 'trip-subtotal' && item?.visible
  );
  const processingFeeItem = sortedBookingTotals.find(
    (item) => item.key === 'trip-totalfees' && item?.visible
  );
  const tripTotalItem = sortedBookingTotals.find(
    (item) => item.key === 'trip-total' && item?.visible
  );
  const paymentAmount = paymentAmountItem?.totalPrice || 0;
  const processingFee = processingFeeItem?.totalPrice || 0;
  const finalAmountToCharge =
    tripTotalItem?.totalPrice || paymentAmount + processingFee;

  const confirmButtonLabel = paymentRecoveryStatus
    ? t('bookings:currencyPayment.finishPayment')
    : getCtaLabel(
        t,
        isFullCapacity,
        activeStepKey === 'payment' && selectedCurrency
          ? formatCheckoutCharge(selectedCurrency)
          : formatCurrency(finalAmountToCharge)
      );

  const isSmallMobile = useMediaQuery('(max-width: 460px)');
  const contactLink = getOperatorContactLink(
    {
      ...(operatorContact || {}),
      email: operatorContact?.email || operatorEmail,
      phone: operatorContact?.phone || booking?.trip?.operatorContact?.phone
    },
    booking?.trip?.tripname
  );

  const handleContactOperator = () => {
    if (contactLink?.href) {
      window.location.href = contactLink.href;
    }
  };

  return (
    <Box
      className='booking-modal__actions'
      data-testid='booking-modal-actions'
      style={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: showBackButton
          ? isSmallMobile
            ? '120px 1fr'
            : '155px 1fr'
          : '1fr',
        gap: showBackButton ? '8px' : '0'
      }}
    >
      {showBackButton && (
        <XGButton
          data-testid='action-next-back-button'
          id='action-next-back-button'
          text={t('back')}
          type='secondary'
          size={isSmallMobile ? 'small' : 'medium'}
          variant='outlined'
          onClick={onCancel}
          disabled={
            disabledBack || isSubmitting || Boolean(paymentRecoveryStatus)
          }
          data-xgfe-stng-close='false'
          style={{ width: '100%', height: '44px' }}
        />
      )}

      <Box sx={{ position: 'relative' }}>
        <XGButton
          data-testid='action-next-confirm-button'
          id='action-next-confirm-button'
          text={confirmButtonLabel}
          type='primary'
          size={isSmallMobile ? 'small' : 'medium'}
          variant='contained'
          onClick={isFullCapacity ? handleContactOperator : onHandleNext}
          disabled={isFullCapacity ? false : disabledConfirm || isSubmitting}
          style={{
            minWidth: '155px',
            width: '100%',
            height: '44px',
            backgroundColor: isFullCapacity ? '#f59e0b' : primaryColor,
            borderColor: isFullCapacity ? '#f59e0b' : primaryColor,
            opacity: isFullCapacity
              ? 1
              : disabledConfirm || isSubmitting
                ? 0.5
                : 1
          }}
          className='w-100'
        />
        {isSubmitting && (
          <CircularProgress
            id='booking-modal-actions-loader'
            size={24}
            sx={{
              color: blue[500],
              position: 'absolute',
              top: '50%',
              left: '50%',
              marginTop: '-12px',
              marginLeft: '-12px'
            }}
          />
        )}
      </Box>
    </Box>
  );
};

BookingPlanActions.propTypes = {
  onCancel: PropTypes.func,
  onHandleNext: PropTypes.func,
  isSubmitting: PropTypes.bool,
  disabledBack: PropTypes.bool,
  disabledConfirm: PropTypes.bool,
  isFullCapacity: PropTypes.bool,
  operatorEmail: PropTypes.string,
  selectedCurrency: PropTypes.object,
  paymentRecoveryStatus: PropTypes.string,
  operatorContact: PropTypes.shape({
    email: PropTypes.string,
    phone: PropTypes.string
  })
};

export default BookingPlanActions;
